import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
} from "@mui/material";
import React from "react";
import { EmptyState } from "../../../components/EmptyState";
import { LocationNavigationHeader } from "../../../components/LocationNavigationHeader";
import { TimeControlBar } from "../../../components/TimeControlBar";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { PredefinedObstacle } from "../../../hooks/usePredefinedObstacle";
import { PlacementService } from "../../../services/PlacementService";
import { Ramp } from "../../../types";
import { MovableObstacleGrid } from "../../../widgets/AddMovableObstacle/MovableObstacleGrid";
import { ScheduleGanttDialog } from "../../Ramp/AutoPark/components/ScheduleGanttDialog";
import { RampPresenterBase } from "../../Ramp/RampPresenterBase";

interface ScheduledRampViewPresenterProps {
  // Core props
  id: string;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;

  // Ramp data
  baseRamp: Ramp | null;
  scheduledRamp: Ramp | null;

  // Time management
  currentTimestamp: Date;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  setTimeRange: (start: Date, end: Date) => void;

  // Loading states
  initialLoading: boolean;
  isUpdating: boolean;
  isPending: boolean;
  hasLoadedOnce: boolean;

  // Navigation
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  isSchedulingEnabled: boolean;
  hasNextTrip: boolean;
  hasPreviousTrip: boolean;

  // Trip info
  selectedTripInfo: {
    start: Date;
    end: Date;
    tailNumber: string;
  } | null;
  tenantTripMap: Map<string, string>;
  placementsInRange: any[];
  tripCountsByDate: Map<string, number>;
  allPlacementsForSearch: any[];
  heatmapPlacements: any[];

  // Obstacle management
  showAddObstacleDialog: boolean;
  setShowAddObstacleDialog: (show: boolean) => void;
  addedObstacles: any[];
  predefinedObstacles: PredefinedObstacle[];

  // Event handlers
  handleTimeChange: (newTime: Date) => void;
  handleJumpToNextTrip: () => Promise<void>;
  handleJumpToPreviousTrip: () => Promise<void>;
  handleUndo: () => void;
  handleRampChange: (updatedRamp: Ramp) => void;
  handleAircraftRemoval: (tenantId: string) => void;
  handleNavigateToLocation: (
    locationId: string,
    locationType: string,
    time: Date
  ) => void;
  setAddedObstaclesWithLogging: (
    newObstaclesOrUpdater: any[] | ((prev: any[]) => any[])
  ) => void;

  // Services
  placementService: PlacementService;

  // Refresh
  refreshKey: number;
  setRefreshKey: (updater: (prev: number) => number) => void;
}

export const ScheduledRampViewPresenter: React.FC<
  ScheduledRampViewPresenterProps
> = ({
  id,
  isReference,
  isAlgorithmTesting,
  baseRamp,
  scheduledRamp,
  currentTimestamp,
  rangeStart,
  rangeEnd,
  setTimeRange,
  initialLoading,
  isUpdating,
  isPending,
  hasLoadedOnce,
  isDrawerOpen,
  setIsDrawerOpen,
  isSchedulingEnabled,
  hasNextTrip,
  hasPreviousTrip,
  selectedTripInfo,
  tenantTripMap,
  placementsInRange,
  tripCountsByDate,
  allPlacementsForSearch,
  heatmapPlacements,
  showAddObstacleDialog,
  setShowAddObstacleDialog,
  addedObstacles,
  predefinedObstacles,
  handleTimeChange,
  handleJumpToNextTrip,
  handleJumpToPreviousTrip,
  handleUndo,
  handleRampChange,
  handleAircraftRemoval,
  handleNavigateToLocation,
  setAddedObstaclesWithLogging,
  placementService,
  setRefreshKey,
}) => {
  // Check if auto parking is enabled (required for View Schedule button)
  const { activeFBO } = useActiveFBOs();
  const isAutoParkEnabled = activeFBO?.settings?.enableAutoParking ?? false;

  // Schedule Gantt dialog state
  const [scheduleGanttOpen, setScheduleGanttOpen] = React.useState(false);

  // Handle time range change from Gantt view
  const handleTimeRangeChange = React.useCallback(
    (newStart: Date, newEnd: Date) => {
      setTimeRange(newStart, newEnd);
    },
    [setTimeRange]
  );

  // Use the actual time range from props for metrics
  const timeRange = React.useMemo(
    () => ({
      start: rangeStart || new Date(),
      end: rangeEnd || new Date(),
    }),
    [rangeStart, rangeEnd]
  );

  // Handle schedule gantt dialog applied changes
  const handleScheduleGanttApplied = React.useCallback(() => {
    // Trigger refresh by incrementing refreshKey
    setRefreshKey((prev) => prev + 1);
  }, [setRefreshKey]);

  return (
    <Stack direction="column" sx={{ height: "100vh" }}>
      {/* Navigation and time controls in unified header */}
      <Stack direction="row">
        {/* Left side: Navigation that will be unified with info card below */}
        <Box
          sx={{
            width: isDrawerOpen ? "400px" : "125px", // Match sidebar width
            minWidth: isDrawerOpen ? "400px" : "125px", // Prevent flex shrinking
            flexShrink: 0, // Disable flexbox shrinking
            p: isDrawerOpen ? 2 : 1, // Less padding when collapsed
            borderRight: 1,
            borderColor: "divider",
          }}
        >
          {isDrawerOpen ? (
            <LocationNavigationHeader
              locationName={baseRamp?.name || ""}
              locationId={id}
              locationType="ramp"
              isSchedulingEnabled={isSchedulingEnabled}
              rightActions={
                <IconButton onClick={() => setIsDrawerOpen(false)}>
                  <KeyboardDoubleArrowLeftIcon />
                </IconButton>
              }
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconButton onClick={() => setIsDrawerOpen(true)}>
                <KeyboardDoubleArrowRightIcon />
              </IconButton>
            </Box>
          )}
          <div style={{ height: 8 }}></div>
          <Divider />
        </Box>

        {/* Right side: Time controls */}
        <Box
          sx={{
            flexGrow: 1,
            pt: 2,
            pb: 3,
          }}
        >
          <TimeControlBar
            currentTime={currentTimestamp}
            onTimeChange={handleTimeChange}
            tripCountsByDate={tripCountsByDate}
            selectedTripStart={selectedTripInfo?.start}
            selectedTripEnd={selectedTripInfo?.end}
            showTripDuration={!!selectedTripInfo}
            selectedTailNumber={selectedTripInfo?.tailNumber}
            onJumpToNextTrip={handleJumpToNextTrip}
            onJumpToPreviousTrip={handleJumpToPreviousTrip}
            showTripJumpButtons={true}
            hasNextTrip={hasNextTrip}
            hasPreviousTrip={hasPreviousTrip}
            showViewScheduleButton={isAutoParkEnabled}
            onViewSchedule={() => setScheduleGanttOpen(true)}
            viewScheduleDisabled={!baseRamp}
          />
        </Box>
      </Stack>

      {/* Subtle loading indicator for background updates - only for non-date changes */}
      {isUpdating && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
          }}
        />
      )}

      {/* Ramp presenter with scheduled data */}
      <Stack
        sx={{
          flex: 1,
          transition: "opacity 0.2s ease-in-out",
          opacity: isPending ? 0.8 : 1,
        }}
      >
        {initialLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 4,
            }}
          >
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton
              variant="rectangular"
              width="90%"
              height={400}
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={2} width="90%">
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
            </Stack>
          </Box>
        ) : scheduledRamp ? (
          <RampPresenterBase
            id={id}
            isReference={isReference}
            isAlgorithmTesting={isAlgorithmTesting}
            ramp={scheduledRamp}
            setRamp={handleRampChange}
            history={{ undo: () => {} }} // Empty history for scheduled view
            isScheduledView={true}
            onUndo={handleUndo}
            scheduledViewProps={{
              tenantTripMap,
              placementService,
              currentTime: currentTimestamp,
              onScheduledUpdate: () => {
                // Trigger a refresh after a short delay to ensure DB has updated
                setTimeout(() => {
                  setRefreshKey((prev) => prev + 1);
                }, 300);
              },
              timeRange: timeRange, // Use the actual calculated time range
              allPlacements: placementsInRange, // Ramp-specific placements for default display
              allPlacementsForSearch: allPlacementsForSearch, // All-location placements for search
              heatmapPlacements, // Placements for 72h heatmap window
              onRemoveAircraft: handleAircraftRemoval,
              onTimeChange: handleTimeChange,
              onNavigateToLocation: handleNavigateToLocation,
            }}
            onScheduledUpdate={() => {
              // Trigger a refresh after a short delay to ensure DB has updated
              setTimeout(() => {
                setRefreshKey((prev) => prev + 1);
              }, 300);
            }}
          />
        ) : hasLoadedOnce ? (
          <EmptyState
            title="No Ramp Data Available"
            description="Unable to load ramp information for this time period. Try refreshing the page or selecting a different time."
            actionLabel="Refresh"
            onAction={() => setRefreshKey((prev) => prev + 1)}
          />
        ) : null}
      </Stack>

      {/* Add Obstacle Dialog */}
      <Dialog
        open={showAddObstacleDialog}
        onClose={() => setShowAddObstacleDialog(false)}
        maxWidth="lg"
      >
        <DialogTitle>Add Movable Obstacle</DialogTitle>
        <DialogContent sx={{ width: 900, height: 600 }}>
          {baseRamp && (
            <MovableObstacleGrid
              location={baseRamp}
              movableObstacles={predefinedObstacles}
              stack={baseRamp.stack}
              setStack={(newStack) => {
                const baseObstacles = baseRamp.stack.movableObstacles || [];
                const currentAdded = addedObstacles;

                // Separate new obstacles from position updates
                const updatedAdded = [];
                const newlyAdded = [];

                for (const obstacle of newStack.movableObstacles) {
                  const isFromBase = baseObstacles.find(
                    (base) => base.id === obstacle.id
                  );
                  const isFromAdded = currentAdded.find(
                    (added) => added.id === obstacle.id
                  );

                  if (isFromBase && baseRamp) {
                    // Base obstacle - position changes handled elsewhere, skip
                    continue;
                  } else if (isFromAdded) {
                    // Existing added obstacle - update position
                    updatedAdded.push(obstacle);
                  } else {
                    // Truly new obstacle
                    newlyAdded.push(obstacle);
                  }
                }

                // Update addedObstacles with position changes + new obstacles
                const finalObstacles = [...updatedAdded, ...newlyAdded];
                setAddedObstaclesWithLogging(finalObstacles);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddObstacleDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setShowAddObstacleDialog(false);
              // Clear added obstacles since they're now saved to database
              setAddedObstaclesWithLogging([]);
              // Trigger refresh to reload from database
              setRefreshKey((prev) => prev + 1);
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Gantt Dialog */}
      {baseRamp && (
        <ScheduleGanttDialog
          open={scheduleGanttOpen}
          onClose={() => setScheduleGanttOpen(false)}
          initialStartTime={rangeStart || new Date()}
          initialEndTime={rangeEnd || new Date()}
          currentTimestamp={currentTimestamp}
          rampId={id}
          rampName={baseRamp.name}
          rampShapes={baseRamp.shapes}
          rampDepth={baseRamp.depth}
          onTimeRangeChange={handleTimeRangeChange}
          zones={baseRamp.shapes
            .filter((s) => s.type === "Zone")
            .map((s) => ({
              id: s.id,
              name: s.text || s.id,
              spotIds: [], // Will be populated from spot metadata
              priority: s.priority || 5,
            }))}
          placementService={placementService}
          onApplied={handleScheduleGanttApplied}
        />
      )}
    </Stack>
  );
};
